import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom"; 
import { doc, getDoc, serverTimestamp, setDoc } from "firebase/firestore";
import { db } from "../firebase/config";
import { useAuth } from "../context/AuthContext";
import { useWeekContext } from "../context/WeekContext";
import { useRecoveryProgram } from "../hooks/useRecoveryProgram";
import { useLessons } from "../hooks/useLessons";
import { ProgressBar } from "./recovery";
import ActivitiesPopup from "./ActivitiesPopup";
import { createLogger } from "../lib/logger";

const log = createLogger("WeeklyProgressCard");

const WeeklyProgressCard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate(); 
  const { unlockedWeek: currentWeek } = useWeekContext();
  const { weekProgram, loading: loadingProgram } = useRecoveryProgram();
  
  // Kept untouched as requested
  const { weeks, progress: progressMap, loading: lessonsLoading } = useLessons();

  const [activitiesCompleted, setActivitiesCompleted] = useState<Record<string, boolean>>({});
  const [showActivitiesPopup, setShowActivitiesPopup] = useState(false);

  const currentActivities = weekProgram[currentWeek]?.activities ?? [];
  const totalActivities = weekProgram[currentWeek]?.activities?.length ?? 0;
  const completedActivities = Object.values(activitiesCompleted).filter(Boolean).length;

  // 1. Corrected: Activities Fetch (Added missing user.uid segment)
  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const ref = doc(
          db,
          "users",
          user.uid,
          "recoveryWeeklyActivities",
          `physical_wellbeing_6w_${currentWeek}`,
        );
        const snap = await getDoc(ref);
        setActivitiesCompleted(
          snap.exists() ? (snap.data()?.activities ?? {}) : {},
        );
      } catch {
        setActivitiesCompleted({});
      }
    })();
  }, [user, currentWeek]);

  // 2. Corrected: Toggle activity + save to Firestore (Added user.uid & isolated async side-effect)
  const toggleActivity = useCallback(
    async (activityId: string) => {
      if (!user) return;

      const updated = { 
        ...activitiesCompleted, 
        [activityId]: !activitiesCompleted[activityId] 
      };

      // Instantly update UI state so clicks feel immediate
      setActivitiesCompleted(updated);

      try {
        const ref = doc(
          db,
          "users",
          user.uid,
          "recoveryWeeklyActivities",
          `physical_wellbeing_6w_${currentWeek}`,
        );
        
        await setDoc(
          ref,
          {
            userId: user.uid,
            programmeId: "physical_wellbeing_6w",
            weekNumber: currentWeek,
            activities: updated,
            updatedAt: serverTimestamp(),
          },
          { merge: true },
        );
      } catch (e) {
        log.error("Error saving activity", {
          error: e instanceof Error ? e.message : String(e),
        });
        // Rollback state if the network request fails completely
        setActivitiesCompleted(activitiesCompleted);
      }
    },
    [user, currentWeek, activitiesCompleted],
  );

  // ── Derive lesson counts dynamically from your filtered weeks object ───
  // Kept untouched as requested
  const { lessonsTotal, lessonsCompleted } = useMemo(() => {
    const currentWeekLessons = weeks[Number(currentWeek)] || [];
    let completed = 0;
    
    for (const l of currentWeekLessons) {
      if (progressMap[l.id]?.status === "completed") {
        completed++;
      }
    }
    return { lessonsTotal: currentWeekLessons.length, lessonsCompleted: completed };
  }, [weeks, progressMap, currentWeek]);

  // ── LOADING STATE: Branded Spinner ────────────────────────────────────────
  if (!user || lessonsLoading || loadingProgram) {
    return (
      <section className="dashboard-card weekly-progress-card" style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '200px',
        backgroundColor: '#fcf9f7'
      }}>
        {/* Spinner */}
        <div style={{
          width: '40px',
          height: '40px',
          border: '4px solid rgba(30, 94, 91, 0.1)',
          borderTop: '4px solid #1e5e5b',
          borderRadius: '50%',
          animation: 'spin 0.8s linear infinite',
          marginBottom: '16px',
          boxSizing: 'border-box'
        }} />

        {/* Branding */}
        <div style={{ fontFamily: 'DM Sans, sans-serif', textAlign: 'center' }}>
          <p style={{
            color: '#0c2624',
            fontSize: '14px',
            fontWeight: '600',
            letterSpacing: '2px',
            textTransform: 'uppercase',
            margin: 0,
            animation: 'pulse 1.5s ease-in-out infinite'
          }}>
            BodyOwn
          </p>
        </div>

        <style>{`
          @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
          @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }
        `}</style>
      </section>
    );
  }

  return (
    <>
      <section className="dashboard-card weekly-progress-card">
        <h1 className="question_1">Your Weekly Progress</h1>

        {/* Weekly Activities block */}
        <div
          className="weeklyactivities"
          onClick={() => setShowActivitiesPopup(true)}
          style={{ cursor: "pointer" }}
        >
          <div className="titleofweeklyactivities">
            Your Weekly Activities (Week {currentWeek})
          </div>
          <div
            style={{
              fontFamily: "'DM Sans'",
              fontWeight: 700,
              fontSize: 14,
              color: "#194B49",
              marginTop: 8,
            }}
          >
            {completedActivities}/{totalActivities} activities completed
          </div>
          <ProgressBar
            value={completedActivities}
            max={totalActivities}
            size="sm"
            className="mt-1"
          />
        </div>

        {/* Weekly Learning block */}
        <div 
          className="weeklyactivities"
          onClick={() => navigate("/recovery", { state: { scrollTo: "learning" } })} 
          style={{ cursor: "pointer" }}
        >
          <div className="titleofweeklyactivities">
            Your Weekly Learning (Week {currentWeek})
          </div>
          <div
            style={{
              fontFamily: "'DM Sans'",
              fontWeight: 700,
              fontSize: 14,
              color: "#194B49",
              marginTop: 8,
            }}
          >
            {lessonsCompleted}/{lessonsTotal} learning steps completed
          </div>
          <ProgressBar
            value={lessonsCompleted}
            max={lessonsTotal}
            size="sm"
            className="mt-1"
          />
        </div>
      </section>

      {showActivitiesPopup && (
        <ActivitiesPopup
          weekNumber={currentWeek}
          activities={currentActivities}
          activitiesCompleted={activitiesCompleted}
          onToggle={toggleActivity}
          onClose={() => setShowActivitiesPopup(false)}
        />
      )}
    </>
  );
};

export default WeeklyProgressCard;